import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * App toast helpers on top of sonner (the shadcn toast). All toast traffic
 * goes through here: the ephemeral neutral toast (uiSlice `showToast`
 * delegates to it) and the persistent stale-content warning
 * (`useStaleWarning` calls it directly). Rendered by the `Toaster` mount in
 * `_app.tsx`.
 */

/** Matches the old IosToast display time. */
const APP_TOAST_DURATION_MS = 1_600;

export type AppToastAction = {
  label: string;
  onAction: () => void;
};

export function showAppToast(message: string, action?: AppToastAction) {
  toast(message, {
    duration: action ? 5_000 : APP_TOAST_DURATION_MS,
    action: action
      ? {
          label: action.label,
          onClick: action.onAction,
        }
      : undefined,
    // Per-call, not Toaster-level: toastOptions.classNames would also leak
    // onto the unstyled stale warning below (sonner merges them into every
    // toast regardless of `unstyled`).
    classNames: {
      toast:
        'rounded-2xl border-border bg-background/95 text-foreground text-xs font-medium shadow-sm backdrop-blur-sm',
    },
  });
}

export type StaleWarningAction = {
  label: string;
  onAction: () => void;
};

// Handle of the currently shown warning. A FRESH id per show is load-bearing:
// sonner defers `toast.dismiss(id)` via requestAnimationFrame and re-creating
// the same id inherits the pending deletion — with a fixed id, the
// dismiss+re-show sequence useStaleWarning emits on item switch (and on every
// dev mount under StrictMode) would kill the replacement toast a frame later.
let staleToastId: string | number | null = null;
// Last id handed to toast.dismiss, kept until the next show so the dismissal
// can be retried after a same-frame re-show (see showStaleWarning).
let lastDismissedId: string | number | null = null;

function StaleWarningContent({
  message,
  action,
}: {
  message: string;
  action?: StaleWarningAction;
}) {
  const { t } = useTranslation();

  const handleAction = () => {
    action?.onAction();
    dismissStaleWarning();
  };

  return (
    // w-[var(--width)] fills sonner's toast slot (the --width custom property
    // set on the toaster, 356px): an unstyled toast gets no width of its own,
    // and without this the absolutely-positioned slot shrink-wraps the text
    // into a narrow off-center column.
    //
    // ENG-1735: every label here is `text-warning-strong`, never `text-warning`
    // — `--warning` is a FILL, and as text on this card it measured 1.79 : 1.
    //
    // The tint ladder is nested, so read the COMPOSITED alpha, not the class.
    // A `/N` child inside this `/10` card composites to `N + 0.10(1−N)`, and
    // `--warning-strong` runs out of contrast at an effective alpha of ~0.245
    // (4.49 at 0.25). That is why the child tints step /10 → /15 rather than
    // the /15 → /25 they used to: /25 inside /10 is an effective 0.325 and
    // scores 4.27. Measured over `bg-background`:
    //   card             /10                 α 0.100 → 4.96
    //   action rest      /10 inside /10      α 0.190 → 4.67
    //   action hover     /15 inside /10      α 0.235 → 4.54
    //   dismiss rest     (transparent)       α 0.100 → 4.96
    //   dismiss hover    /15 inside /10      α 0.235 → 4.54
    // Deepening any of these one more step drops below AA, so raise the text
    // token before you raise a tint here.
    /* eslint-disable-next-line khorium/no-primitive-recipe --
       Not an `Alert`. (1) It is a TOAST: sonner renders it through
       `toast.custom` into an ol that is already an aria-live region and
       announces its own contents, while `alert.tsx` hardcodes `role="alert"`,
       so composing Alert here announces every stale warning twice. (2) The
       shape is a single flex ROW — message, an optional action button and a
       dismiss button, both trailing — and Alert's `grid-cols-[0_1fr]`
       icon/title/description grid has no slot for a trailing action.
       (3) `w-[var(--width)]` is what fills sonner's absolutely-positioned
       toast slot, and `unstyled` toasts get no width otherwise; Alert's
       `w-full` measures against a slot that shrink-wraps. (4) The surface is
       `text-xs` / `px-3 py-2` / `rounded-2xl` + `backdrop-blur-sm` in a
       warning tone, against Alert's `text-sm` / `px-4 py-3` / `rounded-lg`
       and its two variants (`default`, `destructive`), so every class here
       would be an override — including the tints the contrast ladder above
       is measured on. */
    <div className="flex w-[var(--width)] items-center gap-2 rounded-2xl border border-warning/30 bg-warning/10 px-3 py-2 text-xs font-medium text-warning-strong shadow-sm backdrop-blur-sm">
      <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">{message}</span>
      {action && (
        <button
          type="button"
          onClick={handleAction}
          className="shrink-0 rounded-md bg-warning/10 px-2 py-0.5 font-semibold text-warning-strong transition-colors hover:bg-warning/15"
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        onClick={dismissStaleWarning}
        aria-label={t('compCommon.staleWarning.dismiss')}
        className="shrink-0 rounded p-0.5 text-warning-strong transition-colors hover:bg-warning/15"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

/**
 * Persistent, dismissible warning for stale (week-old+) content (ENG-976).
 * Stays until dismissed; `unstyled` because the content carries the full
 * warning look itself. A singleton: showing a new warning dismisses the
 * previous one.
 */
export function showStaleWarning(message: string, action?: StaleWarningAction) {
  const previous = staleToastId ?? lastDismissedId;
  staleToastId = toast.custom(
    () => <StaleWarningContent message={message} action={action} />,
    { duration: Infinity, unstyled: true },
  );
  // Dismiss the predecessor AFTER creating the replacement. A dismiss whose
  // deferred publish is still pending when a create lands gets lost (observed
  // with sonner 2.0.7: the dismissed toast stays on screen and the warnings
  // stack) — re-issuing it here, on the other side of the create, sticks.
  // Dismissing an id that is already gone is a no-op, so the retry is safe.
  if (previous != null && previous !== staleToastId) toast.dismiss(previous);
  lastDismissedId = null;
}

export function dismissStaleWarning() {
  if (staleToastId != null) {
    toast.dismiss(staleToastId);
    // Remember what we just dismissed: if a re-show follows in the same
    // frame (item switch, StrictMode), showStaleWarning re-dismisses it.
    lastDismissedId = staleToastId;
    staleToastId = null;
  }
}
